import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Modal from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  ShieldCheck,
  ShieldAlert,
  RotateCcw,
  Search,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Calendar,
  X,
  Package,
  User,
  ArrowRight,
  Clock,
  ScanBarcode,
  ShoppingBag,
  Check,
} from "lucide-react";
import apiClient from "@/api/apiClient";
import { API_ENDPOINTS } from "@/api/header";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";
import {
  getSaleItemReturnDetails,
  getSaleWarrantySummary,
  calculateWarrantyDates,
} from "@/lib/warrantyUtils";

const PosWarrantyModal = ({
  show,
  onHide,
  currencySymbol = "$",
  onLoadSaleToCart,
  onSelectSaleItemForWarranty,
  currentSaleData = null,
  mode = "pos",
}) => {
  const navigate = useNavigate();
  const currentStore = useSelector((state) => state.current_store);

  // Search state
  const [saleSearch, setSaleSearch] = useState("");
  const [debouncedSaleSearch, setDebouncedSaleSearch] = useState("");
  const [isSearchingSales, setIsSearchingSales] = useState(false);
  const [foundSales, setFoundSales] = useState([]);
  const [selectingSaleId, setSelectingSaleId] = useState(null);

  const searchInputRef = useRef(null);
  const handleBarcodeOrSearchSubmitRef = useRef(null);

  // Focus search input when modal opens
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setSaleSearch("");
      setDebouncedSaleSearch("");
      setFoundSales([]);
      setSelectingSaleId(null);
    }
  }, [show]);

  // Debounce search input for sales
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSaleSearch(saleSearch.trim());
    }, 350);
    return () => clearTimeout(handler);
  }, [saleSearch]);

  // Query sales
  useEffect(() => {
    if (!show || !debouncedSaleSearch) {
      if (!debouncedSaleSearch) setFoundSales([]);
      return;
    }

    let isMounted = true;
    const fetchSales = async () => {
      setIsSearchingSales(true);
      try {
        const res = await apiClient.get(API_ENDPOINTS.SALES.SALES, {
          params: {
            search: debouncedSaleSearch,
            store_id: currentStore?.id,
            per_page: 10,
          },
          timeout: 30000,
        });
        if (isMounted) {
          setFoundSales(res.data?.data || []);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Sale lookup error:", err);
          toast.error("Error searching sales");
        }
      } finally {
        if (isMounted) {
          setIsSearchingSales(false);
        }
      }
    };

    fetchSales();
    return () => {
      isMounted = false;
    };
  }, [debouncedSaleSearch, show, currentStore?.id]);

  // Handle selecting a sale -> loads that sale into POS
  const handleSelectSale = async (sale) => {
    if (selectingSaleId) return;
    setSelectingSaleId(sale.id);

    try {
      if (mode === "pos" && onLoadSaleToCart) {
        await onLoadSaleToCart(sale);
        onHide();
      } else {
        toast.info(
          `Loading Sale #${sale.invoice_number || sale.id} into POS...`,
        );
        onHide();
        navigate(`/pos?sale_id=${sale.id}&warranty=true`);
      }
    } catch (err) {
      console.error("Error selecting sale for warranty:", err);
      toast.error("Failed to load sale into POS");
    } finally {
      setSelectingSaleId(null);
    }
  };

  // Handle selecting a specific item for warranty -> loads sale and opens claim modal
  const handleSelectSaleItem = async (sale, item) => {
    if (selectingSaleId) return;

    if (item) {
      const returnDetails = getSaleItemReturnDetails(item);
      if (returnDetails.isReturned) {
        toast.warning(
          `Cannot create claim: ${returnDetails.reason || "Item already returned"}`,
        );
        return;
      }
    }

    setSelectingSaleId(sale.id);

    try {
      if (mode === "pos" && onSelectSaleItemForWarranty) {
        await onSelectSaleItemForWarranty(sale, item);
        onHide();
      } else if (mode === "pos" && onLoadSaleToCart) {
        await onLoadSaleToCart(sale);
        onHide();
      } else {
        toast.info(
          `Loading Sale #${sale.invoice_number || sale.id} into POS...`,
        );
        onHide();
        navigate(`/pos?sale_id=${sale.id}&warranty=true`);
      }
    } catch (err) {
      console.error("Error selecting item for warranty:", err);
      toast.error("Failed to load item into POS");
    } finally {
      setSelectingSaleId(null);
    }
  };

  // Barcode Scan / Immediate Submission
  const handleBarcodeOrSearchSubmit = async (rawCode) => {
    const code = (rawCode || "").trim();
    if (!code) return;
    const cleanCode = code.replace(/^#/, "").trim();
    if (!cleanCode) return;

    setIsSearchingSales(true);
    try {
      const params = {
        page: 1,
        per_page: 5,
        search: cleanCode,
      };
      if (currentStore?.id) params.store_id = currentStore.id;

      const res = await apiClient.get(API_ENDPOINTS.SALES.SALES, { params });
      const list = res.data?.data || [];

      const lower = cleanCode.toLowerCase();
      let matchedSale = list.find((s) => {
        const inv = (s.invoice_number || "").toLowerCase().replace(/^#/, "");
        const cartNo = (s.cart_number || "").toLowerCase().replace(/^#/, "");
        return inv === lower || cartNo === lower || String(s.id) === cleanCode;
      });

      if (!matchedSale && list.length === 1) {
        matchedSale = list[0];
      }

      if (!matchedSale) {
        try {
          const detailRes = await apiClient.get(
            API_ENDPOINTS.SALES.SALE_DETAILS(cleanCode),
          );
          if (detailRes.data?.data) matchedSale = detailRes.data.data;
          else if (detailRes.data) matchedSale = detailRes.data;
        } catch {
          // not direct id
        }
      }

      if (matchedSale) {
        await handleSelectSale(matchedSale);
        return;
      }

      toast.error(`No sale found matching "${code}"`);
    } catch (err) {
      console.error("Barcode sale lookup error:", err);
      toast.error(`Failed to lookup sale for barcode "${code}"`);
    } finally {
      setIsSearchingSales(false);
    }
  };

  handleBarcodeOrSearchSubmitRef.current = handleBarcodeOrSearchSubmit;

  // Global barcode listener when modal is open
  useEffect(() => {
    if (!show) return;
    let scanBuffer = "";
    let lastKeyTime = 0;

    const handleGlobalScan = (e) => {
      if (document.activeElement === searchInputRef.current) {
        return;
      }

      const now = Date.now();
      if (e.key === "Enter" || e.keyCode === 13) {
        if (scanBuffer.length >= 2 && now - lastKeyTime < 250) {
          e.preventDefault();
          const code = scanBuffer.trim();
          scanBuffer = "";
          if (code) {
            handleBarcodeOrSearchSubmitRef.current?.(code);
          }
        } else {
          scanBuffer = "";
        }
        return;
      }

      if (e.key && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        if (now - lastKeyTime > 80) {
          scanBuffer = e.key;
        } else {
          scanBuffer += e.key;
        }
        lastKeyTime = now;
      }
    };

    window.addEventListener("keydown", handleGlobalScan);
    return () => window.removeEventListener("keydown", handleGlobalScan);
  }, [show]);

  const getItemWarrantyBadge = (item) => {
    const returnDetails = getSaleItemReturnDetails(item);
    if (returnDetails.isReturned) {
      return (
        <Badge
          variant="outline"
          className="text-[10px] py-0 h-4 text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/10 font-semibold gap-1"
          title={returnDetails.reason}
        >
          <RotateCcw className="h-2.5 w-2.5 text-amber-600 dark:text-amber-400" />
          Returned ({returnDetails.label})
        </Badge>
      );
    }

    const prodCandidate = item.product || item.product_data;
    const dates = calculateWarrantyDates(
      prodCandidate,
      item.warranty_start_date || item.created_at || new Date(),
    );

    const endDateStr = item.warranty_end_date || dates.endDate;
    if (dates.isNoWarranty || (dates.startDate === dates.endDate && !item.warranty_end_date)) {
      return (
        <Badge
          variant="outline"
          className="text-[10px] py-0 h-4 text-amber-700 dark:text-amber-400 border-amber-500/30 bg-amber-500/10 font-medium"
        >
          No Warranty
        </Badge>
      );
    }

    if (!endDateStr) {
      return (
        <Badge
          variant="outline"
          className="text-[10px] py-0 h-4 text-muted-foreground font-normal"
        >
          No Warranty Info
        </Badge>
      );
    }

    const end = new Date(endDateStr);
    const now = new Date();
    if (end >= now) {
      return (
        <Badge
          variant="outline"
          className="text-[10px] py-0 h-4 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/10 font-medium"
        >
          Active (until {formatDate(endDateStr)})
        </Badge>
      );
    } else {
      return (
        <Badge
          variant="outline"
          className="text-[10px] py-0 h-4 text-rose-600 dark:text-rose-400 border-rose-500/30 bg-rose-500/10 font-medium"
        >
          Expired ({formatDate(endDateStr)})
        </Badge>
      );
    }
  };

  return (
    <Modal
      open={show}
      onOpenChange={(v) => !v && onHide()}
      title={
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-bold text-foreground">
              Warranty - Search Sale
            </div>
            <div className="text-xs text-muted-foreground font-normal">
              Find original sale invoice to load into POS for warranty processing
            </div>
          </div>
        </div>
      }
      maxWidth="max-w-3xl"
      footer={
        <div className="flex justify-between items-center w-full">
          <span className="text-xs text-muted-foreground">
            Selecting a sale loads its items into the POS cart for warranty claim creation.
          </span>
          <Button variant="outline" size="sm" onClick={onHide}>
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Banner if a sale is ALREADY loaded in POS */}
        {currentSaleData && (
          <div className="p-3 rounded-xl border border-purple-500/30 bg-purple-500/10 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-purple-500/20 text-purple-600 flex items-center justify-center shrink-0">
                <Check className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-foreground">
                    Sale #{currentSaleData.invoice_number || currentSaleData.id}
                  </span>
                  <Badge variant="outline" className="text-[10px] py-0 h-4 bg-background">
                    Loaded in POS
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  Customer:{" "}
                  <strong className="text-foreground">
                    {currentSaleData.customer?.full_name ||
                      currentSaleData.customer?.name ||
                      "Walk-in Customer"}
                  </strong>{" "}
                  • Total: {currencySymbol}
                  {parseFloat(currentSaleData.total || 0).toFixed(2)}
                </p>
              </div>
            </div>

            <Button
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white font-medium text-xs h-8 gap-1.5 shrink-0"
              onClick={() => {
                onHide();
                if (onSelectSaleItemForWarranty) {
                  onSelectSaleItemForWarranty(currentSaleData, null);
                }
              }}
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Use Loaded Sale
            </Button>
          </div>
        )}

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            type="text"
            placeholder="Search by Invoice # (e.g. INV-001), Barcode, Serial #, Customer Name / Phone..."
            value={saleSearch}
            onChange={(e) => setSaleSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && saleSearch.trim()) {
                e.preventDefault();
                handleBarcodeOrSearchSubmit(saleSearch.trim());
              }
            }}
            className="pl-10 pr-9 h-10 text-sm bg-muted/20"
          />
          {saleSearch && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={() => setSaleSearch("")}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {/* Found sales list */}
        <div className="min-h-[260px] max-h-[420px] overflow-y-auto space-y-3 pr-1">
          {isSearchingSales ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
              <Loader2 className="h-7 w-7 animate-spin text-purple-500" />
              <p className="text-xs">Searching sales records...</p>
            </div>
          ) : !debouncedSaleSearch ? (
            <div className="flex flex-col items-center justify-center py-14 text-center text-muted-foreground gap-3">
              <div className="h-14 w-14 rounded-2xl bg-purple-500/10 flex items-center justify-center text-purple-600">
                <Search className="h-7 w-7" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  Search Sold Products
                </p>
                <p className="text-xs text-muted-foreground max-w-sm mt-1">
                  Enter an invoice number, serial number, customer name, or scan a receipt barcode
                  above to locate the sale for warranty claim.
                </p>
              </div>
            </div>
          ) : foundSales.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center text-muted-foreground gap-2">
              <AlertCircle className="h-8 w-8 text-amber-500" />
              <p className="text-sm font-medium text-foreground">
                No matching sales found
              </p>
              <p className="text-xs">
                No completed sale matches &quot;{debouncedSaleSearch}&quot;.
              </p>
            </div>
          ) : (
            foundSales.map((sale) => {
              const isSelectedLoading = selectingSaleId === sale.id;
              const warrantySummary = getSaleWarrantySummary(sale);

              return (
                <Card
                  key={sale.id}
                  className="border shadow-none overflow-hidden hover:border-purple-500/40 transition-colors"
                >
                  {/* Sale Header */}
                  <div className="bg-muted/40 px-3.5 py-2.5 flex flex-wrap items-center justify-between gap-2 border-b text-xs">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-foreground">
                        Sale #{sale.invoice_number || sale.id}
                      </span>
                      <span className="text-muted-foreground">
                        • {formatDate(sale.created_at || sale.date)}
                      </span>
                      <span className="text-muted-foreground">
                        • Customer:{" "}
                        <strong className="text-foreground">
                          {sale.customer?.full_name ||
                            sale.customer?.name ||
                            sale.customer?.customer_name ||
                            "Walk-in"}
                        </strong>
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      {warrantySummary.hasReturned && (
                        <Badge
                          variant="outline"
                          className="text-[10px] py-0 h-5 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold gap-1"
                        >
                          <RotateCcw className="h-2.5 w-2.5 text-amber-600" />
                          {warrantySummary.allReturned
                            ? "All Returned"
                            : `${warrantySummary.returnedItems} Returned`}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px] py-0 h-5 font-semibold">
                        {currencySymbol}
                        {parseFloat(sale.total || 0).toFixed(2)}
                      </Badge>
                      <Button
                        size="sm"
                        disabled={isSelectedLoading}
                        className="h-7 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white gap-1"
                        onClick={() => handleSelectSale(sale)}
                      >
                        {isSelectedLoading ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Check className="h-3 w-3" />
                        )}
                        Select Sale
                      </Button>
                    </div>
                  </div>

                  {/* Sale Items List */}
                  <CardContent className="p-3 space-y-2">
                    {sale.items && sale.items.length > 0 ? (
                      sale.items.map((item) => {
                        const returnDetails = getSaleItemReturnDetails(item);
                        let serial =
                          item.inventory_serials?.serial_number ||
                          (Array.isArray(item.inventory_serials) && item.inventory_serials[0]?.serial_number) ||
                          item.inventory_serial?.serial_number ||
                          item.serial_number ||
                          returnDetails.serialNumber ||
                          "";
                        if (!serial && item.serial_numbers) {
                          try {
                            const parsed = JSON.parse(item.serial_numbers);
                            if (Array.isArray(parsed) && parsed.length > 0) {
                              serial = parsed[0];
                            }
                          } catch {
                            serial = item.serial_numbers;
                          }
                        }

                        return (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-2 rounded-lg bg-background border hover:bg-muted/30 transition-colors gap-3"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-xs text-foreground truncate">
                                  {item.product?.name ||
                                    item.product_data?.name ||
                                    item.name ||
                                    "Product"}
                                </span>
                                {serial && (
                                  <Badge
                                    variant="secondary"
                                    className={`text-[10px] font-mono py-0 h-4 ${returnDetails.isReturned
                                      ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30"
                                      : ""
                                      }`}
                                  >
                                    SN: {serial}
                                  </Badge>
                                )}
                                {getItemWarrantyBadge(item)}
                              </div>
                              <div className="text-[11px] text-muted-foreground mt-0.5">
                                Qty: {item.quantity} • Unit Price:{" "}
                                {currencySymbol}
                                {parseFloat(item.unit_price || 0).toFixed(2)}
                              </div>
                            </div>

                            {returnDetails.isReturned ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled
                                className="h-7 text-xs font-normal opacity-50 cursor-not-allowed gap-1 text-muted-foreground border-dashed shrink-0"
                                title={
                                  returnDetails.reason ||
                                  "This item has already been returned / claimed under warranty"
                                }
                              >
                                <ShieldAlert className="h-3.5 w-3.5 text-amber-600" />
                                Already Claimed
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={isSelectedLoading}
                                className="h-7 text-xs font-semibold gap-1 text-purple-700 dark:text-purple-300 hover:bg-purple-500/20 shrink-0"
                                onClick={() => handleSelectSaleItem(sale, item)}
                                title="Load this sale into POS and file warranty claim for this item"
                              >
                                <ShieldCheck className="h-3.5 w-3.5 text-purple-600" />
                                Claim Warranty
                              </Button>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-xs text-muted-foreground italic py-1">
                        No item breakdown recorded for this sale.
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
};

PosWarrantyModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string,
  onLoadSaleToCart: PropTypes.func,
  onSelectSaleItemForWarranty: PropTypes.func,
  currentSaleData: PropTypes.object,
  mode: PropTypes.oneOf(["pos", "repair"]),
};

export default PosWarrantyModal;
